import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';

const ConfirmDialogContext = createContext(null);

export const ConfirmDialogProvider = ({ children }) => {
  const resolverRef = useRef(null);
  const [dialogState, setDialogState] = useState({
    open: false,
    title: '',
    message: '',
    confirmLabel: 'Confirm',
    cancelLabel: 'Cancel',
    danger: false,
  });

  const closeWithResult = useCallback((confirmed) => {
    if (resolverRef.current) {
      resolverRef.current(confirmed);
      resolverRef.current = null;
    }
    setDialogState((prev) => ({ ...prev, open: false }));
  }, []);

  const confirm = useCallback((options = {}) => {
    const {
      title = 'Please Confirm',
      message = 'Are you sure you want to continue?',
      confirmLabel = 'Confirm',
      cancelLabel = 'Cancel',
      danger = false,
    } = options;

    setDialogState({
      open: true,
      title,
      message,
      confirmLabel,
      cancelLabel,
      danger,
    });

    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const contextValue = useMemo(() => ({ confirm }), [confirm]);

  return (
    <ConfirmDialogContext.Provider value={contextValue}>
      {children}
      <Dialog open={dialogState.open} onClose={() => closeWithResult(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{dialogState.title}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{dialogState.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => closeWithResult(false)}>{dialogState.cancelLabel}</Button>
          <Button
            variant="contained"
            color={dialogState.danger ? 'warning' : 'primary'}
            onClick={() => closeWithResult(true)}
          >
            {dialogState.confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </ConfirmDialogContext.Provider>
  );
};

export const useConfirmDialog = () => {
  const context = useContext(ConfirmDialogContext);
  if (!context) {
    throw new Error('useConfirmDialog must be used within ConfirmDialogProvider');
  }
  return context;
};
